import { Control, Controller, UseFormRegister, FieldErrors } from "react-hook-form";
import { AsyncSelectField } from "./AsyncSelectField";
import { Input } from "@/components/common/Input";
import { Select } from "@/components/common/Select";
import { Label } from "@/components/common/FormLabel";
import { ErrorLabel } from "@/components/common/ErrorLabel";
import { getFieldErrorMessage } from "@/utils/reactHookForm";
import { udNumberValidateRule } from "@/utils/fieldValidation";

export type UserDefinedItem = {
  id: string;
  name: string;
  /** "String" | "Integer" | "Float" | "Date" | "Boolean" */
  format: string;
  /** Number of decimal places allowed. Only set when format is "Float". */
  decimalPlaces: number | null;
  isLookup: boolean;
  lookupValues: string[] | null;
  isAutoComplete?: boolean;
  isMandatory?: boolean;
  isReadOnly?: boolean;
};

type SelectOption = { value: string; label: string };

type UserDefinedItemFieldProps = {
  item: UserDefinedItem;
  fieldKey: string;
  control: Control<any>;
  register: UseFormRegister<any>;
  errors: FieldErrors<any>;
  loadOptions?: (inputValue: string) => Promise<SelectOption[]>;
};

export const UserDefinedItemField = ({ item, fieldKey, control, register, errors, loadOptions }: UserDefinedItemFieldProps) => {
  const isReadOnly = item.isReadOnly ?? false;
  const isMandatory = item.isMandatory ?? true;
  const required = isMandatory ? { required: `${item.name} is required` } : {};
  const mandatoryMarker = isMandatory ? <span className="text-red-500">*</span> : null;

  if (item.isLookup) {
    if (item.isAutoComplete && loadOptions && control) {
      return (
        <div>
          <Label forField={fieldKey} label={<>{item.name} {mandatoryMarker}</>} />
          <AsyncSelectField<SelectOption>
            name={fieldKey}
            control={control}
            rules={isMandatory ? { required: `${item.name} is required` } : undefined}
            inputId={fieldKey}
            loadOptions={loadOptions}
            isClearable
            isDisabled={isReadOnly}
            placeholder="Type to search..."
          />
          <ErrorLabel error={getFieldErrorMessage(errors, fieldKey)} />
        </div>
      );
    }
    return (
      <div>
        <Label forField={fieldKey} label={<>{item.name} {mandatoryMarker}</>} />
        <Select
          id={fieldKey}
          disabled={isReadOnly}
          inErrorState={Boolean(errors[fieldKey])}
          {...register(fieldKey, required)}
        >
          {item.lookupValues?.map((v) => (
            <option key={v} value={v}>{v}</option>
          ))}
        </Select>
        <ErrorLabel error={getFieldErrorMessage(errors, fieldKey)} />
      </div>
    );
  }

  if (item.format === "Boolean") {
    return (
      <div>
        <Label forField={fieldKey} label={<>{item.name} {mandatoryMarker}</>} />
        <div className="flex items-center gap-2 mt-1">
          <Controller
            name={fieldKey}
            control={control}
            defaultValue=""
            render={({ field }) => {
              const value = field.value ?? "";
              const isUnset = value === "";
              const isChecked = value === "1";
              const nextValue = isUnset ? "0" : isChecked ? "" : "1";
              return (
                <>
                  <input
                    type="checkbox"
                    id={fieldKey}
                    ref={(element) => {
                      if (element) element.indeterminate = isUnset;
                      field.ref(element);
                    }}
                    checked={isChecked}
                    disabled={isReadOnly}
                    className="h-4 w-4"
                    style={isUnset ? { accentColor: "#9ca3af" } : undefined}
                    onBlur={field.onBlur}
                    onChange={() => field.onChange(nextValue)}
                  />
                  <span className={`text-sm ${isUnset ? "text-gray-400 italic" : "text-gray-700"}`}>
                    {isUnset ? "Not set" : isChecked ? "Yes" : "No"}
                  </span>
                </>
              );
            }}
          />
        </div>
      </div>
    );
  }

  if (item.format === "Date") {
    return (
      <div>
        <Label forField={fieldKey} label={<>{item.name} {mandatoryMarker}</>} />
        <Input
          id={fieldKey}
          type="date"
          disabled={isReadOnly}
          inErrorState={Boolean(errors[fieldKey])}
          {...register(fieldKey, required)}
        />
        <ErrorLabel error={getFieldErrorMessage(errors, fieldKey)} />
      </div>
    );
  }

  // Float uses type="text" to avoid browser native step validation interfering with our regex
  const inputType = item.format === "Integer" ? "number" : "text";
  const numberRules = (item.format === "Integer" || item.format === "Float")
    ? { ...required, validate: udNumberValidateRule(item.format, item.decimalPlaces, item.name) }
    : required;
  return (
    <div>
      <Label forField={fieldKey} label={<>{item.name} {mandatoryMarker}</>} />
      <Input id={fieldKey} type={inputType} disabled={isReadOnly} inErrorState={Boolean(errors[fieldKey])} {...register(fieldKey, numberRules)} />
      <ErrorLabel error={getFieldErrorMessage(errors, fieldKey)} />
    </div>
  );
};
